import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import './index.css';
import './setup';

// Importing Routes
import { BunkeringDashboardRoute } from './routes/BunkeringDashboard';
import Settings from './routes/Settings';
import PasswordReset from './routes/PasswordReset';
import Login from './routes/Login';
import App from './app/App';
import ProtectedRoute from './app/ProtectedRoute';
import LoginCallback from './routes/oidc/LoginCallback';
import LogoutCallback from './routes/oidc/LogoutCallback';
import LoginCloud from './routes/LoginCloud';
import { BunkeringLogsRoute } from './routes/BunkeringLogs';
import { AlarmLogsRoute } from './routes/AlarmLogs';
import { MFMComparisonRoute } from './routes/MfmComparison';

import { AppUrls } from './config/appRoutes';
import { isCloud } from './utils/urlUtils';

// Helper function to create dynamic routes for logs
const createLogRoutes = (basePath: string, RouteComponent: React.FC) => [
  {
    path: basePath,
    element: <RouteComponent />,
  },
  {
    path: `${basePath}/:deviceIdBmt`,
    element: <RouteComponent />,
  },
  {
    path: `${basePath}/:deviceIdBmt/:drawerTab`,
    element: <RouteComponent />,
  },
];

// Base Route Configuration
const baseRoutes = [
  {
    path: '/',
    element: <App />,
    children: [
      // Root redirect to dashboard
      {
        path: '/',
        element: <Navigate to={AppUrls.BUNKERING_DASHBOARD} />,
      },
      {
        path: '/login/cloud',
        element: <LoginCloud />,
      },
      {
        path: '/login/edge',
        element: isCloud() ? <LoginCloud /> : <Login />,
      },

      // Protected Routes
      {
        path: '/',
        element: <ProtectedRoute />,
        children: [
          {
            path: AppUrls.BUNKERING_DASHBOARD,
            element: <BunkeringDashboardRoute />,
          },
          {
            path: AppUrls.MFM_COMPARISON,
            element: <MFMComparisonRoute />,
          },
          {
            path: AppUrls.ALARM_LOGS,
            children: createLogRoutes(AppUrls.ALARM_LOGS, AlarmLogsRoute),
          },
          {
            path: AppUrls.BUNKERING_LOGS,
            children: createLogRoutes(AppUrls.BUNKERING_LOGS, BunkeringLogsRoute),
          },
          {
            path: AppUrls.SETTINGS,
            element: <Settings />,
          },
          {
            path: '/reset-password',
            element: !isCloud() ? <PasswordReset /> : <Navigate to={AppUrls.BUNKERING_DASHBOARD} />,
          }
        ],
      },
    ],
  },

  // OIDC Callbacks
  {
    path: '/oidc-signin-callback',
    element: <LoginCallback />,
  },
  {
    path: '/oidc-signout-callback',
    element: <LogoutCallback />,
  },
];

const rootElement = document.getElementById('root');

// Ensure the rootElement exists
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(<RouterProvider router={createBrowserRouter(baseRoutes)} />);
} else {
  console.error('Root element not found!');
}
